import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { format, set, addDays, addBusinessDays } from 'date-fns';
import {
  CalendarClock,
  Check,
  Plus,
  Calendar as CalendarIcon,
  User as UserIcon,
  MessageSquare,
  Pencil,
  AlertCircle,
  Trash2,
  ArrowRight,
  Sparkles,
  Loader2,
  UserCog,
} from 'lucide-react';
import { toast } from 'sonner';
import * as z from 'zod';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';

import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useAllClerkUsers } from '@/hooks/use-clerk-user';
import { useUpdateContact } from '@/hooks/use-contacts';
import { useExtractContactChanges } from '@/hooks/use-contacts-extraction';
import type { ContactsExtractionResponse } from '@/hooks/use-contacts-extraction';
import { useDeal, DEALS_QUERY_KEY } from '@/hooks/use-deals';
import type { Deal } from '@/hooks/use-deals';
import { useCreateTask, useTasks, useUpdateTask, useAddTaskMessage, useUpdateTaskMessage, useDeleteTaskMessage, useDeleteTask } from '@/hooks/use-tasks';
import type { Task } from '@/hooks/use-tasks';
import { cn } from '@/lib/utils';
import { TimeSelect } from './time-select';

/**
 * Quick check if a task is a contact verification task (for showing the button).
 * The actual parsing is done by the AI-based backend endpoint.
 */
function isContactVerificationTask(taskContent: string): boolean {
  return taskContent.includes('[Contact Verification]')
    && !taskContent.toLowerCase().includes('no changes needed')
    && !taskContent.toLowerCase().includes('information is correct');
}

function AutoResizingTextarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const adjustHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  useEffect(() => {
    adjustHeight();
  }, [props.value]);

  return <textarea {...props} ref={textareaRef} onInput={adjustHeight} />;
}

const formSchema = z.object({
  task: z.string().min(1, 'Task description is required.'),
  due: z.date({
    required_error: 'A due date is required.',
  }),
  dueTime: z.object({
    hours: z.string(),
    minutes: z.string(),
    period: z.enum(['AM', 'PM']),
  }),
  assigned_to: z.array(z.string()).min(1, 'At least one assignee is required.'),
});

const defaultFormValues = {
  task: '',
  assigned_to: [],
  due: addDays(new Date(), 1),
  dueTime: {
    hours: '09',
    minutes: '00',
    period: 'AM' as const,
  },
};

// Contact Update Dialog Props
interface ContactUpdateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  extractionResult: ContactsExtractionResponse | null;
  deal: Deal | undefined;
  task: Task | null;
  onApply: () => Promise<void>;
  isApplying: boolean;
  isExtracting: boolean;
}

// Confidence badge colors
const CONFIDENCE_COLORS: Record<string, string> = {
  high: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  low: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
};

// Thresholds for collapsing lists
const MAX_DOMAINS_SHOWN = 5;
const MAX_CONTACTS_SHOWN = 10;
const MAX_DOMAINS_PER_CONTACT = 3;

function ContactUpdateDialog({
  open,
  onOpenChange,
  extractionResult,
  deal,
  task,
  onApply,
  isApplying,
  isExtracting,
}: ContactUpdateDialogProps) {
  if (!deal)
    return null;

  // Find the contacts that match the domains in the extraction result
  const matchingContacts = extractionResult?.domains
    ? deal.registrants.people.filter((person) =>
        person.associated_domains.some((domain) => extractionResult.domains.includes(domain)),
      )
    : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="size-5 text-primary" />
            Apply Contact Changes
          </DialogTitle>
          <DialogDescription>
            {isExtracting
              ? 'Analyzing task content with AI...'
              : (extractionResult?.hasChanges
                  ? (
                      <>
                        {extractionResult.verifierType === 'registrant' ? 'Registrant Contact' : 'Verification Contact'}
                        {' '}
                        submitted changes for
                        {' '}
                        <strong>
                          {extractionResult.domains.length}
                          {' '}
                          domain
                          {extractionResult.domains.length === 1 ? '' : 's'}
                        </strong>
                      </>
                    )
                  : 'No changes detected in this task.')}
          </DialogDescription>
        </DialogHeader>

        {isExtracting && (
          <div className="flex items-center justify-center py-12">
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="size-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">
                Extracting contact changes using AI...
              </p>
            </div>
          </div>
        )}

        {!isExtracting && extractionResult?.hasChanges && (
          <div className="space-y-4 py-4">
            {/* Show affected domains - collapsible for large lists */}
            {extractionResult.domains.length > 0 && (
              <Accordion type="single" collapsible defaultValue={extractionResult.domains.length <= MAX_DOMAINS_SHOWN ? 'domains' : undefined}>
                <AccordionItem value="domains" className="border-none">
                  <AccordionTrigger className="py-0 text-sm font-medium text-muted-foreground hover:no-underline">
                    <span className="flex items-center gap-2">
                      Changes will apply to
                      {' '}
                      {extractionResult.domains.length}
                      {' '}
                      domain
                      {extractionResult.domains.length === 1 ? '' : 's'}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-0 pt-2">
                    <ScrollArea className={extractionResult.domains.length > 20 ? 'h-32' : undefined}>
                      <div className="flex flex-wrap gap-1">
                        {extractionResult.domains.map((domain) => (
                          <span
                            key={domain}
                            className="inline-flex items-center rounded-md bg-primary/10 px-2 py-1 text-xs font-medium text-primary"
                          >
                            {domain}
                          </span>
                        ))}
                      </div>
                    </ScrollArea>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            )}

            {/* Show matching contacts - scrollable for large lists */}
            {matchingContacts.length > 0 && (
              <div className="space-y-2">
                <div className="text-sm font-medium text-muted-foreground">
                  Contact records to be updated (
                  {matchingContacts.reduce((acc, c) => acc + c.contact_ids.length, 0)}
                  {' '}
                  total):
                </div>
                <ScrollArea className={matchingContacts.length > MAX_CONTACTS_SHOWN ? 'h-48' : undefined}>
                  <div className="space-y-1 pr-2">
                    {matchingContacts.map((contact, index) => {
                      // Show which domains this contact is associated with that are in the extraction
                      const affectedDomains = contact.associated_domains.filter(
                        (d) => extractionResult.domains.includes(d),
                      );
                      const showAllDomains = affectedDomains.length <= MAX_DOMAINS_PER_CONTACT;
                      const displayedDomains = showAllDomains ? affectedDomains : affectedDomains.slice(0, MAX_DOMAINS_PER_CONTACT);
                      const hiddenCount = affectedDomains.length - MAX_DOMAINS_PER_CONTACT;

                      return (
                        <div key={index} className="rounded-md bg-muted/50 px-3 py-2 text-sm">
                          <div className="flex items-center justify-between">
                            <span className="font-medium">{contact.legal_name}</span>
                            <span className="text-xs text-muted-foreground">
                              {contact.contact_ids.length}
                              {' '}
                              record
                              {contact.contact_ids.length > 1 ? 's' : ''}
                              {' '}
                              ·
                              {affectedDomains.length}
                              {' '}
                              domain
                              {affectedDomains.length === 1 ? '' : 's'}
                            </span>
                          </div>
                          {contact.organization_name && (
                            <div className="text-muted-foreground">{contact.organization_name}</div>
                          )}
                          <div className="mt-1 flex flex-wrap gap-1">
                            {displayedDomains.map((domain) => (
                              <span
                                key={domain}
                                className="inline-flex items-center rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground"
                              >
                                {domain}
                              </span>
                            ))}
                            {!showAllDomains && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="inline-flex cursor-help items-center rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                                      +
                                      {hiddenCount}
                                      {' '}
                                      more
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent side="bottom" className="max-w-xs">
                                    <div className="flex flex-wrap gap-1">
                                      {affectedDomains.slice(MAX_DOMAINS_PER_CONTACT).map((domain) => (
                                        <span key={domain} className="text-xs">{domain}</span>
                                      ))}
                                    </div>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </div>
            )}

            {/* Show the changes with confidence levels */}
            <div className="space-y-2">
              <div className="text-sm font-medium text-muted-foreground">
                Detected changes (via AI):
              </div>
              <div className="rounded-md border">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="px-3 py-2 text-left font-medium">Field</th>
                      <th className="px-3 py-2 text-left font-medium">New Value</th>
                      <th className="px-3 py-2 text-left font-medium">Confidence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {extractionResult.changes.map((change, index) => (
                      <tr key={index} className={index % 2 === 0 ? '' : 'bg-muted/30'}>
                        <td className="px-3 py-2 text-muted-foreground">
                          {change.displayField}
                        </td>
                        <td className="px-3 py-2 font-medium">
                          {change.value}
                        </td>
                        <td className="px-3 py-2">
                          <span className={cn(
                            'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                            CONFIDENCE_COLORS[change.confidence] || CONFIDENCE_COLORS.medium,
                          )}
                          >
                            {change.confidence}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* AI reasoning */}
            {extractionResult.aiReason && (
              <div className="rounded-md bg-muted/50 p-3 text-xs text-muted-foreground">
                <span className="font-medium">AI Note:</span>
                {' '}
                {extractionResult.aiReason}
              </div>
            )}

            {matchingContacts.length === 0 && (
              <div className="rounded-md border border-yellow-500 bg-yellow-50 p-3 text-sm dark:bg-yellow-950">
                <div className="flex items-center gap-2 text-yellow-700 dark:text-yellow-400">
                  <AlertCircle className="size-4" />
                  <span>No matching contacts found for the specified domains.</span>
                </div>
              </div>
            )}
          </div>
        )}

        {!isExtracting && !extractionResult?.hasChanges && (
          <div className="py-8 text-center text-sm text-muted-foreground">
            {extractionResult?.isCorrect
              ? 'This task indicates the contact information is correct (no changes needed).'
              : 'Unable to detect any contact changes in this task.'}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isApplying || isExtracting}
          >
            Cancel
          </Button>
          <Button
            onClick={onApply}
            disabled={isApplying || isExtracting || !extractionResult?.hasChanges || matchingContacts.length === 0}
          >
            {isApplying
              ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" />
                    Applying...
                  </>
                )
              : (
                  <>
                    <UserCog className="mr-2 size-4" />
                    Apply Changes
                  </>
                )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TasksPanel() {
  const [isCreating, setIsCreating] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [expandedTaskId, setExpandedTaskId] = useState<string | null>(null);
  const [chatMessage, setChatMessage] = useState('');
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editedMessage, setEditedMessage] = useState('');
  const [contactUpdateDialogOpen, setContactUpdateDialogOpen] = useState(false);
  const [selectedTaskForUpdate, setSelectedTaskForUpdate] = useState<Task | null>(null);
  const [extractionResult, setExtractionResult] = useState<ContactsExtractionResponse | null>(null);
  const [isApplyingChanges, setIsApplyingChanges] = useState(false);
  const { dealId } = useParams();
  const { userId } = useAuth();
  const queryClient = useQueryClient();
  const { data: tasks, isLoading } = useTasks();
  const { data: deal } = useDeal(dealId!);
  const { data: users } = useAllClerkUsers();
  const { mutate: createTask, isPending: isCreatingTask } = useCreateTask();
  const { mutate: updateTask } = useUpdateTask();
  const { mutate: addMessage } = useAddTaskMessage();
  const { mutate: updateMessage } = useUpdateTaskMessage();
  const { mutate: deleteMessage } = useDeleteTaskMessage();
  const { mutate: deleteTask } = useDeleteTask();
  const { mutateAsync: updateContact } = useUpdateContact();
  const { mutateAsync: extractContactChanges, isPending: isExtracting } = useExtractContactChanges();

  // Handler for opening the contact update dialog and triggering AI extraction
  const handleOpenContactUpdateDialog = async (task: Task) => {
    setSelectedTaskForUpdate(task);
    setExtractionResult(null);
    setContactUpdateDialogOpen(true);

    try {
      const result = await extractContactChanges({ taskContent: task.task });
      setExtractionResult(result);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to extract contact changes';
      toast.error(errorMessage);
    }
  };

  // Handler for applying contact changes
  const handleApplyContactChanges = async () => {
    if (!extractionResult || !deal || !extractionResult.hasChanges)
      return;

    setIsApplyingChanges(true);

    try {
      // Find contacts that match the domains
      const matchingContacts = deal.registrants.people.filter((person) =>
        person.associated_domains.some((domain) => extractionResult.domains.includes(domain)),
      );

      if (matchingContacts.length === 0) {
        toast.error('No matching contacts found for the specified domains');
        setIsApplyingChanges(false);
        return;
      }

      // Build the update payload from extracted changes
      const updatePayload: Record<string, string | null> = {};
      for (const change of extractionResult.changes) {
        updatePayload[change.field] = change.value || null;
      }

      // Update each matching contact
      const updatePromises = matchingContacts.flatMap((contact) =>
        contact.contact_ids.map(async (contactId) => {
          // Build complete contact data for update
          const contactData = {
            legal_name: contact.legal_name,
            email: contact.email,
            phone_number: contact.phone_number,
            alt_phone: contact.alt_phone,
            fax_number: contact.fax_number,
            address_line_1: contact.address_line_1,
            address_line_2: contact.address_line_2,
            address_line_3: contact.address_line_3,
            city: contact.city,
            state_province: contact.state_province,
            postal_code: contact.postal_code,
            country: contact.country,
            alt_name: contact.alt_name,
            alt_email: contact.alt_email,
            title: contact.title,
            organization_name: contact.organization_name,
            c_suite: contact.c_suite,
            sole_prop: contact.sole_prop,
            new_legal_name: contact.new_legal_name,
            // Apply the changes from the verification task
            ...updatePayload,
          };

          return updateContact({ id: contactId, ...contactData });
        }),
      );

      await Promise.all(updatePromises);

      // Invalidate deal queries to refresh contact data
      await queryClient.invalidateQueries({ queryKey: [...DEALS_QUERY_KEY, dealId] });

      toast.success(`Successfully updated ${matchingContacts.length} contact(s)`);
      setContactUpdateDialogOpen(false);
      setSelectedTaskForUpdate(null);
      setExtractionResult(null);

      // Mark the task as done
      if (selectedTaskForUpdate) {
        updateTask({
          id: selectedTaskForUpdate.id,
          done: new Date().toISOString(),
        });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to apply contact changes';
      toast.error(errorMessage);
    } finally {
      setIsApplyingChanges(false);
    }
  };

  const dealTasks = (tasks?.filter((task) => task.deal_id === deal?.id) ?? [])
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  const createForm = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      task: '',
      assigned_to: userId ? [userId] : [],
      due: addBusinessDays(new Date(), 2),
      dueTime: {
        hours: '09',
        minutes: '00',
        period: 'AM' as const,
      },
    },
  });

  const editForm = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      task: '',
      assigned_to: [],
      due: addBusinessDays(new Date(), 2),
      dueTime: {
        hours: '09',
        minutes: '00',
        period: 'AM' as const,
      },
    },
  });

  // Reset create form when creating new task
  useEffect(() => {
    if (isCreating) {
      createForm.reset({
        task: '',
        assigned_to: userId ? [userId] : [],
        due: addBusinessDays(new Date(), 2),
        dueTime: {
          hours: '09',
          minutes: '00',
          period: 'AM' as const,
        },
      });
    }
  }, [isCreating, createForm, userId]);

  function onCreateSubmit(values: z.infer<typeof formSchema>) {
    if (!deal?.id)
      return;

    // Convert 12-hour time to 24-hour time
    let hours = Number.parseInt(values.dueTime.hours);
    if (values.dueTime.period === 'PM' && hours !== 12) {
      hours += 12;
    } else if (values.dueTime.period === 'AM' && hours === 12) {
      hours = 0;
    }

    const dueDate = set(values.due, {
      hours,
      minutes: Number.parseInt(values.dueTime.minutes),
      seconds: 0,
      milliseconds: 0,
    });

    createTask(
      {
        deal_id: deal.id,
        task: values.task,
        due: dueDate.toISOString(),
        assigned_to: values.assigned_to,
      },
      {
        onSuccess: () => {
          toast.success('Task created');
          createForm.reset(defaultFormValues);
          setIsCreating(false);
        },
        onError: (error) => {
          toast.error(error.message);
        },
      },
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4 p-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-[200px]" />
          <Skeleton className="h-9 w-[100px]" />
        </div>
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-[100px] w-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full max-h-[600px] flex-col overflow-hidden bg-background">
      <div className="flex items-center justify-between border-b p-4">
        <div>
          <h2 className="text-lg font-semibold">Tasks</h2>
          <p className="text-sm text-muted-foreground">
            {dealTasks.length}
            {' '}
            task
            {dealTasks.length === 1 ? '' : 's'}
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => setIsCreating(true)}
          className="gap-2"
        >
          <Plus className="size-4" />
          Add Task
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-2">
          {isCreating && (
            <Form {...createForm}>
              <form onSubmit={createForm.handleSubmit(onCreateSubmit)} className="space-y-4 rounded-lg border bg-card p-4">
                <FormField
                  control={createForm.control}
                  name="task"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <textarea
                          {...field}
                          className="w-full bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none"
                          placeholder="What needs to be done?"
                          rows={2}
                          autoFocus
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <div className="flex items-center gap-2">
                  <FormField
                    control={createForm.control}
                    name="due"
                    render={({ field }) => (
                      <FormItem className="flex-none">
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant="outline"
                                size="sm"
                                className={cn(
                                  'gap-2',
                                  !field.value && 'text-muted-foreground',
                                )}
                              >
                                <CalendarIcon className="size-4" />
                                {field.value
                                  ? format(
                                      set(field.value, {
                                        hours: createForm.getValues('dueTime').period === 'PM' && createForm.getValues('dueTime').hours !== '12'
                                          ? Number(createForm.getValues('dueTime').hours) + 12
                                          : (createForm.getValues('dueTime').period === 'AM' && createForm.getValues('dueTime').hours === '12'
                                              ? 0
                                              : Number(createForm.getValues('dueTime').hours)),
                                        minutes: Number(createForm.getValues('dueTime').minutes),
                                      }),
                                      'MMM d, h:mm a',
                                    )
                                  : 'Due date'}
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-[280px] p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={field.value}
                              onSelect={field.onChange}
                              disabled={(date) =>
                                date < new Date(new Date().setHours(0, 0, 0, 0))}
                              initialFocus
                            />
                            <div className="border-t">
                              <FormField
                                control={createForm.control}
                                name="dueTime"
                                render={({ field }) => (
                                  <div className="p-3">
                                    <FormLabel className="text-xs text-muted-foreground">Time</FormLabel>
                                    <TimeSelect field={field} />
                                  </div>
                                )}
                              />
                            </div>
                          </PopoverContent>
                        </Popover>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={createForm.control}
                    name="assigned_to"
                    render={({ field }) => (
                      <FormItem className="flex-none">
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant="outline"
                                size="sm"
                                className={cn(
                                  'gap-2',
                                  field.value.length === 0 && 'text-muted-foreground',
                                )}
                              >
                                <UserIcon className="size-4" />
                                {field.value.length === 0
                                  ? 'Assign to'
                                  : `${field.value.length} assigned`}
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-[350px] p-0" align="start">
                            <Command>
                              <CommandInput placeholder="Search users..." />
                              <CommandEmpty>No users found.</CommandEmpty>
                              <CommandGroup>
                                {users?.map((user) => (
                                  user && (
                                    <CommandItem
                                      key={user.id}
                                      onSelect={() => {
                                        const isSelected = field.value.includes(user.id ?? '');
                                        if (isSelected) {
                                          field.onChange(field.value.filter((id) => id !== user.id));
                                        } else {
                                          field.onChange([...field.value, user.id ?? '']);
                                        }
                                      }}
                                    >
                                      <div className="flex items-center gap-2">
                                        <div
                                          className={cn(
                                            'flex size-4 items-center justify-center rounded-sm border',
                                            field.value.includes(user.id ?? '')
                                              ? 'border-primary bg-primary text-primary-foreground'
                                              : 'border-muted',
                                          )}
                                        >
                                          {field.value.includes(user.id ?? '') && (
                                            <Check className="size-3" />
                                          )}
                                        </div>
                                        <div className="flex flex-col gap-0.5">
                                          <span className="text-sm">
                                            {user.firstName}
                                            {' '}
                                            {user.lastName}
                                          </span>
                                          <span className="text-xs text-muted-foreground">
                                            {user.primaryEmailAddress?.emailAddress}
                                          </span>
                                        </div>
                                      </div>
                                    </CommandItem>
                                  )
                                ))}
                              </CommandGroup>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      </FormItem>
                    )}
                  />
                  <div className="flex-1" />
                  <Button
                    type="submit"
                    size="sm"
                    disabled={isCreatingTask}
                  >
                    Add Task
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      createForm.reset(defaultFormValues);
                      setIsCreating(false);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </Form>
          )}
          {dealTasks.length === 0 && !isCreating
            ? (
                <div className="flex h-[200px] items-center justify-center rounded-lg border border-dashed">
                  <div className="text-center">
                    <CalendarClock className="mx-auto size-8 text-muted-foreground" />
                    <h3 className="mt-2 font-medium">No tasks yet</h3>
                    <p className="text-sm text-muted-foreground">
                      Create a task to get started
                    </p>
                  </div>
                </div>
              )
            : (
                dealTasks.map((task) => {
                  const dueDate = new Date(task.due);
                  const now = new Date();
                  const isOverdue = !task.done && dueDate < now;

                  return (
                    <div
                      key={task.id}
                      className={cn(
                        'group flex items-start gap-3 rounded-lg border bg-card p-4 transition-colors hover:bg-accent/50',
                        task.done && 'bg-muted hover:bg-muted/80',
                        isOverdue && 'border-destructive/50 bg-destructive/5 hover:border-destructive hover:bg-destructive/10',
                      )}
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          updateTask({
                            id: task.id,
                            done: task.done ? null : new Date().toISOString(),
                          });
                        }}
                        className={cn(
                          'mt-1 flex size-4 shrink-0 items-center justify-center rounded-sm border border-primary ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                          task.done
                            ? 'bg-primary text-primary-foreground'
                            : 'hover:bg-primary/10',
                        )}
                      >
                        <Check className={cn('size-3', !task.done && 'invisible')} />
                      </button>
                      <div className="min-w-0 flex-1 space-y-3">
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0 flex-1">
                            <p className={cn('text-sm whitespace-pre-wrap break-words', task.done && 'text-muted-foreground line-through')}>
                              {task.task}
                            </p>
                            <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <CalendarIcon className="size-3" />
                                {format(new Date(task.created_at), 'MMM d, h:mm a')}
                              </span>
                              <ArrowRight className="size-3" />
                              <span className="flex items-center gap-1">
                                <CalendarClock className="size-3" />
                                {format(dueDate, 'MMM d, h:mm a')}
                              </span>
                              {isOverdue && !task.done && (
                                <Badge variant="destructive" className="gap-1 rounded-sm px-1 py-0 text-[10px] font-medium">
                                  <AlertCircle className="size-3" />
                                  Overdue
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="flex shrink-0 items-center gap-4">
                            <div className="flex items-center gap-2">
                              {task.assigned_to_users?.map((user, index) => (
                                user && (
                                  <div
                                    key={user.id}
                                    className="group relative"
                                    onMouseEnter={(e) => {
                                      const content = e.currentTarget.querySelector('[data-hover-content]');
                                      if (content)
                                        content.classList.remove('opacity-0', 'pointer-events-none');
                                    }}
                                    onMouseLeave={(e) => {
                                      const content = e.currentTarget.querySelector('[data-hover-content]');
                                      if (content)
                                        content.classList.add('opacity-0', 'pointer-events-none');
                                    }}
                                  >
                                    <Avatar
                                      className={cn(
                                        'size-5 shrink-0 cursor-pointer rounded-full border-2 border-background',
                                        task.done && 'opacity-50',
                                      )}
                                    >
                                      <AvatarImage
                                        src={user.imageUrl}
                                        alt={`${user.firstName} ${user.lastName}`}
                                      />
                                      <AvatarFallback className="text-xs">
                                        {user.firstName?.[0]}
                                        {user.lastName?.[0]}
                                      </AvatarFallback>
                                    </Avatar>
                                    <div
                                      data-hover-content
                                      className="pointer-events-none absolute -top-16 left-1/2 z-50 min-w-max -translate-x-1/2 rounded-md bg-popover px-3 py-2 text-popover-foreground opacity-0 shadow-md transition-opacity"
                                    >
                                      <div className="flex flex-col gap-0.5">
                                        <span className="font-medium">
                                          {user.firstName}
                                          {' '}
                                          {user.lastName}
                                        </span>
                                        <span className="text-xs text-muted-foreground">
                                          {user.primaryEmailAddress?.emailAddress}
                                        </span>
                                      </div>
                                      <div className="absolute -bottom-1 left-1/2 size-2 -translate-x-1/2 rotate-45 bg-popover" />
                                    </div>
                                  </div>
                                )
                              ))}
                            </div>
                            <div className="flex items-center gap-1">
                              {/* Apply Contact Changes button for verification tasks */}
                              {isContactVerificationTask(task.task) && !task.done && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="size-7 text-primary hover:text-primary"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          void handleOpenContactUpdateDialog(task);
                                        }}
                                      >
                                        <Sparkles className="size-3" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      Apply contact changes using AI
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                              <Button
                                variant="ghost"
                                size="icon"
                                className="size-7"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingTaskId(editingTaskId === task.id ? null : task.id);
                                  setExpandedTaskId(null);
                                  if (editingTaskId !== task.id) {
                                    const dueDate = new Date(task.due);
                                    const hours = dueDate.getHours();
                                    const minutes = dueDate.getMinutes();
                                    const period = hours >= 12 ? 'PM' : 'AM';
                                    const hour12 = hours % 12 || 12;

                                    editForm.reset({
                                      task: task.task,
                                      due: dueDate,
                                      dueTime: {
                                        hours: hour12.toString().padStart(2, '0'),
                                        minutes: minutes.toString().padStart(2, '0'),
                                        period,
                                      },
                                      assigned_to: task.assigned_to,
                                    });
                                  }
                                }}
                              >
                                <Pencil className="size-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className={cn(
                                  'size-7 relative',
                                  expandedTaskId === task.id && 'bg-accent',
                                )}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExpandedTaskId(expandedTaskId === task.id ? null : task.id);
                                  setEditingTaskId(null);
                                }}
                              >
                                <MessageSquare className="size-3" />
                                {task.chat_history.length > 0 && (
                                  <div className="absolute -right-1 -top-1 flex size-3 items-center justify-center rounded-full bg-primary text-[10px] text-primary-foreground">
                                    {task.chat_history.length}
                                  </div>
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="size-7 text-destructive hover:text-destructive"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const confirm = async () => new Promise<boolean>((resolve) => {
                                    toast.custom(() => (
                                      <div className="flex flex-col gap-2 rounded-lg border bg-card p-4 shadow-lg">
                                        <p className="font-medium">Delete Task</p>
                                        <p className="text-sm text-muted-foreground">Are you sure you want to delete this task? This will also delete all chat messages.</p>
                                        <div className="flex justify-end gap-2">
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => {
                                              toast.dismiss();
                                              resolve(false);
                                            }}
                                          >
                                            Cancel
                                          </Button>
                                          <Button
                                            variant="destructive"
                                            size="sm"
                                            onClick={() => {
                                              toast.dismiss();
                                              resolve(true);
                                            }}
                                          >
                                            Delete
                                          </Button>
                                        </div>
                                      </div>
                                    ), { duration: Infinity });
                                  });

                                  void confirm().then((confirmed) => {
                                    if (confirmed) {
                                      deleteTask(task.id, {
                                        onSuccess: () => {
                                          toast.success('Task deleted');
                                          setEditingTaskId(null);
                                          setExpandedTaskId(null);
                                        },
                                        onError: (error: Error) => {
                                          toast.error(error.message);
                                        },
                                      });
                                    }
                                  });
                                }}
                              >
                                <Trash2 className="size-3" />
                              </Button>
                            </div>
                          </div>
                        </div>
                        {editingTaskId === task.id && (
                          <Form {...editForm}>
                            <form
                              onSubmit={(e) => {
                                e.preventDefault();
                                const values = editForm.getValues();

                                // Convert 12-hour time to 24-hour time
                                let hours = Number.parseInt(values.dueTime.hours);
                                if (values.dueTime.period === 'PM' && hours !== 12) {
                                  hours += 12;
                                } else if (values.dueTime.period === 'AM' && hours === 12) {
                                  hours = 0;
                                }

                                const dueDate = set(values.due, {
                                  hours,
                                  minutes: Number.parseInt(values.dueTime.minutes),
                                  seconds: 0,
                                  milliseconds: 0,
                                });

                                updateTask(
                                  {
                                    id: task.id,
                                    task: values.task,
                                    due: dueDate.toISOString(),
                                    assigned_to: values.assigned_to,
                                  },
                                  {
                                    onSuccess: () => {
                                      toast.success('Task updated');
                                      setEditingTaskId(null);
                                    },
                                    onError: (error) => {
                                      toast.error(error.message);
                                    },
                                  },
                                );
                              }}
                              className="space-y-4"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <FormField
                                control={editForm.control}
                                name="task"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormControl>
                                      <AutoResizingTextarea
                                        {...field}
                                        className="w-full rounded-md border bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                        placeholder="What needs to be done?"
                                        rows={2}
                                      />
                                    </FormControl>
                                  </FormItem>
                                )}
                              />
                              <div className="flex items-center gap-2">
                                <FormField
                                  control={editForm.control}
                                  name="due"
                                  render={({ field }) => (
                                    <FormItem className="flex-none">
                                      <Popover>
                                        <PopoverTrigger asChild>
                                          <FormControl>
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              className={cn(
                                                'gap-2',
                                                !field.value && 'text-muted-foreground',
                                              )}
                                            >
                                              <CalendarIcon className="size-4" />
                                              {field.value
                                                ? format(
                                                    set(field.value, {
                                                      hours: editForm.getValues('dueTime').period === 'PM' && editForm.getValues('dueTime').hours !== '12'
                                                        ? Number(editForm.getValues('dueTime').hours) + 12
                                                        : (editForm.getValues('dueTime').period === 'AM' && editForm.getValues('dueTime').hours === '12'
                                                            ? 0
                                                            : Number(editForm.getValues('dueTime').hours)),
                                                      minutes: Number(editForm.getValues('dueTime').minutes),
                                                    }),
                                                    'MMM d, h:mm a',
                                                  )
                                                : 'Due date'}
                                            </Button>
                                          </FormControl>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-[280px] p-0" align="start">
                                          <Calendar
                                            mode="single"
                                            selected={field.value}
                                            onSelect={field.onChange}
                                            initialFocus
                                          />
                                          <div className="border-t">
                                            <FormField
                                              control={editForm.control}
                                              name="dueTime"
                                              render={({ field }) => (
                                                <div className="p-3">
                                                  <FormLabel className="text-xs text-muted-foreground">Time</FormLabel>
                                                  <TimeSelect field={field} />
                                                </div>
                                              )}
                                            />
                                          </div>
                                        </PopoverContent>
                                      </Popover>
                                    </FormItem>
                                  )}
                                />
                                <FormField
                                  control={editForm.control}
                                  name="assigned_to"
                                  render={({ field }) => (
                                    <FormItem className="flex-none">
                                      <Popover>
                                        <PopoverTrigger asChild>
                                          <FormControl>
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              className={cn(
                                                'gap-2',
                                                field.value.length === 0 && 'text-muted-foreground',
                                              )}
                                            >
                                              <UserIcon className="size-4" />
                                              {field.value.length === 0
                                                ? 'Assign to'
                                                : `${field.value.length} assigned`}
                                            </Button>
                                          </FormControl>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-[350px] p-0" align="start">
                                          <Command>
                                            <CommandInput placeholder="Search users..." />
                                            <CommandEmpty>No users found.</CommandEmpty>
                                            <CommandGroup>
                                              {users?.map((user) => (
                                                user && (
                                                  <CommandItem
                                                    key={user.id}
                                                    onSelect={() => {
                                                      const isSelected = field.value.includes(user.id ?? '');
                                                      if (isSelected) {
                                                        field.onChange(field.value.filter((id) => id !== user.id));
                                                      } else {
                                                        field.onChange([...field.value, user.id ?? '']);
                                                      }
                                                    }}
                                                  >
                                                    <div className="flex items-center gap-2">
                                                      <div
                                                        className={cn(
                                                          'flex size-4 items-center justify-center rounded-sm border',
                                                          field.value.includes(user.id ?? '')
                                                            ? 'border-primary bg-primary text-primary-foreground'
                                                            : 'border-muted',
                                                        )}
                                                      >
                                                        {field.value.includes(user.id ?? '') && (
                                                          <Check className="size-3" />
                                                        )}
                                                      </div>
                                                      <div className="flex flex-col gap-0.5">
                                                        <span className="text-sm">
                                                          {user.firstName}
                                                          {' '}
                                                          {user.lastName}
                                                        </span>
                                                        <span className="text-xs text-muted-foreground">
                                                          {user.primaryEmailAddress?.emailAddress}
                                                        </span>
                                                      </div>
                                                    </div>
                                                  </CommandItem>
                                                )
                                              ))}
                                            </CommandGroup>
                                          </Command>
                                        </PopoverContent>
                                      </Popover>
                                    </FormItem>
                                  )}
                                />
                                <div className="flex-1" />
                                <Button type="submit" size="sm">
                                  Save
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setEditingTaskId(null)}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </form>
                          </Form>
                        )}
                        {expandedTaskId === task.id && (
                          <div className="space-y-3 rounded-md bg-muted/50 p-3 pb-4">
                            <div className="space-y-3">
                              {task.chat_history.map((message) => (
                                <div key={message.id} className="flex items-start gap-2">
                                  <Avatar className="size-6 shrink-0 rounded-full">
                                    <AvatarImage
                                      src={message.user?.imageUrl}
                                      alt={`${message.user?.firstName} ${message.user?.lastName}`}
                                    />
                                    <AvatarFallback className="text-xs">
                                      {message.user?.firstName?.[0]}
                                      {message.user?.lastName?.[0]}
                                    </AvatarFallback>
                                  </Avatar>
                                  <div className="flex-1">
                                    <div className="flex items-center justify-between gap-2">
                                      <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium">
                                          {message.user?.firstName}
                                          {' '}
                                          {message.user?.lastName}
                                        </span>
                                        <span className="text-xs text-muted-foreground">
                                          {format(new Date(message.timestamp), 'MMM d, h:mm a')}
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="size-6"
                                          onClick={() => {
                                            setEditingMessageId(message.id);
                                            setEditedMessage(message.message);
                                          }}
                                        >
                                          <Pencil className="size-3" />
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="size-6 text-destructive hover:text-destructive"
                                          onClick={() => {
                                            const confirm = async () => new Promise<boolean>((resolve) => {
                                              toast.custom(() => (
                                                <div className="flex flex-col gap-2 rounded-lg border bg-card p-4 shadow-lg">
                                                  <p className="font-medium">Delete Message</p>
                                                  <p className="text-sm text-muted-foreground">Are you sure you want to delete this message?</p>
                                                  <div className="flex justify-end gap-2">
                                                    <Button
                                                      variant="ghost"
                                                      size="sm"
                                                      onClick={() => {
                                                        toast.dismiss();
                                                        resolve(false);
                                                      }}
                                                    >
                                                      Cancel
                                                    </Button>
                                                    <Button
                                                      variant="destructive"
                                                      size="sm"
                                                      onClick={() => {
                                                        toast.dismiss();
                                                        resolve(true);
                                                      }}
                                                    >
                                                      Delete
                                                    </Button>
                                                  </div>
                                                </div>
                                              ), { duration: Infinity });
                                            });

                                            void confirm().then((confirmed) => {
                                              if (confirmed) {
                                                deleteMessage(
                                                  {
                                                    taskId: task.id,
                                                    messageId: message.id,
                                                  },
                                                  {
                                                    onSuccess: () => {
                                                      toast.success('Message deleted');
                                                    },
                                                    onError: (error: Error) => {
                                                      toast.error(error.message);
                                                    },
                                                  },
                                                );
                                              }
                                            });
                                          }}
                                        >
                                          <Trash2 className="size-3" />
                                        </Button>
                                      </div>
                                    </div>
                                    {editingMessageId === message.id
                                      ? (
                                          <form
                                            onSubmit={(e) => {
                                              e.preventDefault();
                                              if (!editedMessage.trim())
                                                return;
                                              updateMessage(
                                                {
                                                  taskId: task.id,
                                                  messageId: message.id,
                                                  message: editedMessage.trim(),
                                                },
                                                {
                                                  onSuccess: () => {
                                                    setEditingMessageId(null);
                                                    setEditedMessage('');
                                                  },
                                                  onError: (error: Error) => {
                                                    toast.error(error.message);
                                                  },
                                                },
                                              );
                                            }}
                                            className="mt-0.5 flex items-center gap-2"
                                          >
                                            <input
                                              type="text"
                                              className="flex-1 rounded-md border bg-background px-3 py-1.5 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                              value={editedMessage}
                                              onChange={(e) => setEditedMessage(e.target.value)}
                                              autoFocus
                                            />
                                            <Button type="submit" size="sm">
                                              Save
                                            </Button>
                                            <Button
                                              type="button"
                                              variant="ghost"
                                              size="sm"
                                              onClick={() => {
                                                setEditingMessageId(null);
                                                setEditedMessage('');
                                              }}
                                            >
                                              Cancel
                                            </Button>
                                          </form>
                                        )
                                      : (
                                          <p className="mt-0.5 text-sm">{message.message}</p>
                                        )}
                                  </div>
                                </div>
                              ))}
                            </div>
                            <form
                              onSubmit={(e) => {
                                e.preventDefault();
                                if (!chatMessage.trim())
                                  return;

                                addMessage(
                                  {
                                    taskId: task.id,
                                    message: chatMessage.trim(),
                                  },
                                  {
                                    onSuccess: () => {
                                      setChatMessage('');
                                    },
                                    onError: (error: Error) => {
                                      toast.error(error.message);
                                    },
                                  },
                                );
                              }}
                              className="flex items-center gap-2 pt-2"
                            >
                              <input
                                type="text"
                                className="flex-1 rounded-md border bg-background px-3 py-1.5 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                placeholder="Type a message..."
                                value={chatMessage}
                                onChange={(e) => setChatMessage(e.target.value)}
                              />
                              <Button
                                type="submit"
                                size="sm"
                                disabled={!chatMessage.trim()}
                              >
                                Send
                              </Button>
                            </form>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
        </div>
      </div>

      {/* Contact Update Dialog */}
      <ContactUpdateDialog
        open={contactUpdateDialogOpen}
        onOpenChange={(open) => {
          setContactUpdateDialogOpen(open);
          if (!open) {
            setSelectedTaskForUpdate(null);
            setExtractionResult(null);
          }
        }}
        extractionResult={extractionResult}
        deal={deal}
        task={selectedTaskForUpdate}
        onApply={handleApplyContactChanges}
        isApplying={isApplyingChanges}
        isExtracting={isExtracting}
      />
    </div>
  );
}
